<template>
  <div class="notice-toast-stack">
    <transition-group name="notice-slide" tag="div" class="notice-toast-list">
      <notice-toast-item
        v-for="item in visibleList"
        :key="item.id"
        :message="item"
        @close="handleClose"
        @later="handleLater"
        @read="handleRead"
        @view-order="handleViewOrder"
        @copy-order="handleCopyOrder"
      />
    </transition-group>
  </div>
</template>

<script>
import noticeApi from '@/api/notice.js'
import NoticeToastItem from './NoticeToastItem.vue'
import { parseOrderNoticePayload } from '@/utils/noticeHelper'

export default {
  name: 'NoticeToastStack',
  components: { NoticeToastItem },
  data() {
    return {
      queue: [],
      visible: null,
      isAutoMode: false,
      isPlaying: false,
      /** 本会话已「稍后处理-全部」跳过的消息，避免 WS 再拉取后重复弹窗 */
      suppressedIds: {}
    }
  },
  computed: {
    visibleList() {
      return this.visible ? [this.visible] : []
    }
  },
  methods: {
    /** 自动推送：按队列逐条展示 */
    enqueue(list) {
      if (!list || !list.length) return
      const exists = new Set([
        ...this.queue.map(m => m.id),
        ...(this.visible ? [this.visible.id] : [])
      ])
      list.forEach(msg => {
        if (msg && msg.id && !exists.has(msg.id) && !this.suppressedIds[msg.id]) {
          this.queue.push(msg)
          exists.add(msg.id)
        }
      })
      // 当前无展示中的卡片且队列有数据时，继续弹出（避免 isPlaying 卡住导致必须刷新）
      if (!this.visible && this.queue.length) {
        if (!this.isPlaying) {
          this.isPlaying = true
          this.isAutoMode = true
        }
        this.showNext()
      }
    },
    /** 手动从消息列表打开单条 */
    showOne(msg, { markRead = false } = {}) {
      this.queue = []
      this.isPlaying = false
      this.isAutoMode = false
      this.visible = msg
      if (markRead && msg && msg.id) {
        this.markRead(msg.id).then(() => {
          this.$emit('notice-read', msg.id)
        })
      }
    },
    showNext() {
      if (this.visible) return
      if (!this.queue.length) {
        this.isPlaying = false
        this.isAutoMode = false
        this.$emit('queue-finished')
        return
      }
      const next = this.queue.shift()
      this.visible = next
      this.$emit('notice-displayed', next.id)
    },
    markRead(id) {
      return noticeApi.readNotice(id).then(res => {
        if (res.code !== 200) {
          console.error('标记已读失败', res.msg)
        }
        return res
      })
    },
    finishCurrent({ markRead = false } = {}) {
      const msg = this.visible
      if (!msg) return Promise.resolve()

      const done = () => {
        this.visible = null
        if (this.isAutoMode) {
          this.$nextTick(() => this.showNext())
        } else {
          this.isPlaying = false
        }
      }

      if (markRead && msg.id) {
        return this.markRead(msg.id)
          .then(() => {
            this.$emit('notice-read', msg.id)
            done()
          })
          .catch(() => done())
      }
      done()
      return Promise.resolve()
    },
    handleClose() {
      this.finishCurrent({ markRead: this.isAutoMode })
    },
    handleLater(payload) {
      const clearAll = !!(payload && payload.all)
      if (clearAll) {
        const ids = this.queue.map(m => m.id).filter(Boolean)
        if (this.visible && this.visible.id) {
          ids.push(this.visible.id)
        }
        const next = { ...this.suppressedIds }
        ids.forEach(id => {
          next[id] = true
        })
        this.suppressedIds = next
        this.queue = []
        this.isAutoMode = false
        this.isPlaying = false
        this.visible = null
        Promise.all(ids.map(id => this.markRead(id).catch(() => {}))).finally(() => {
          this.$emit('notices-batch-read', ids)
          this.$emit('queue-finished')
        })
        return
      }
      this.finishCurrent({ markRead: this.isAutoMode })
    },
    handleRead() {
      this.finishCurrent({ markRead: true })
    },
    handleViewOrder() {
      const msg = this.visible
      if (!msg) return
      const type = Number(msg.noticeType)
      const payload = this.parsePayload(msg) || {}
      const orderId = payload.orderId || ''
      const orderNo = payload.orderNo || ''
      const from = (type === 3 || type === 6) ? 'businessOrders' : 'myOrders'

      this.finishCurrent({ markRead: true })
      this.$emit('close-drawer')

      if (!orderId) {
        const path = (type === 3 || type === 6) ? '/businessOrders' : '/myOrders'
        this.$router.push({
          path,
          query: orderNo ? { orderNo } : {}
        }).catch(() => {})
        this.$message.warning(orderNo
          ? `该消息缺少订单ID，请在列表中打开订单 ${orderNo} 的详情`
          : '该消息缺少订单ID，请在列表中打开详情')
        return
      }
      const route = this.$router.resolve({
        path: '/OrderDetails',
        query: { id: orderId, from }
      })
      window.open(route.href, '_blank')
    },
    handleCopyOrder() {
      const msg = this.visible
      if (!msg) return
      const payload = this.parsePayload(msg) || {}
      const orderId = payload.orderId || ''

      this.finishCurrent({ markRead: true })
      this.$emit('close-drawer')

      if (!orderId) {
        const orderNo = payload.orderNo || ''
        this.$router.push({
          path: '/myOrders',
          query: orderNo ? { orderNo, action: 'copy' } : { action: 'copy' }
        }).catch(() => {})
        this.$message.warning(orderNo
          ? `该消息缺少订单ID，请在我的订舱中复制订单 ${orderNo}`
          : '该消息缺少订单ID，请在我的订舱中复制订单重下')
        return
      }
      const route = this.$router.resolve({
        path: '/MakeBooking',
        query: { orderId, mode: 'copy', from: 'notice' }
      })
      window.open(route.href, '_blank')
    },
    parsePayload(msg) {
      return parseOrderNoticePayload(msg)
    },
    /** 终止自动队列（用户手动打开消息时） */
    clearQueue() {
      this.queue = []
      this.isPlaying = false
      this.isAutoMode = false
    }
  }
}
</script>

<style scoped>
.notice-toast-stack {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 3000;
  pointer-events: none;
}
.notice-toast-list {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}
.notice-slide-enter-active,
.notice-slide-leave-active {
  transition: all 0.35s ease;
}
.notice-slide-enter {
  transform: translateX(calc(100% + 24px));
  opacity: 0;
}
.notice-slide-leave-to {
  transform: translateX(calc(100% + 24px));
  opacity: 0;
}
.notice-slide-move {
  transition: transform 0.35s ease;
}
</style>
